import { createContext, useContext, useState, useCallback, ReactNode } from 'react';

export interface ToastOptions {
  title: string;
  /** `info` is the neutral one: `Toast.module.css` already styles `toast_info` (card ground, brand mark). */
  variant?: 'success' | 'error' | 'loading' | 'info';
  duration?: number;
}

export interface Toast extends ToastOptions {
  id: string;
}

interface ToastContextType {
  toasts: Toast[];
  showToast: (options: ToastOptions) => string;
  hideToast: (id: string) => void;
  promise: <T>(
    promise: Promise<T>,
    options: {
      loading: { title: string };
      success: (data: T) => { title: string };
      error: (error: Error) => { title: string };
    }
  ) => Promise<T>;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((options: ToastOptions) => {
    const id = Math.random().toString(36).substring(7);
    const toast: Toast = { ...options, id };
    
    setToasts((prev) => [...prev, toast]);

    if (options.duration !== 0 && options.variant !== 'loading') {
      setTimeout(() => {
        hideToast(id);
      }, options.duration || 4000);
    }

    return id;
  }, []);

  const hideToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const promise = useCallback(async <T,>(
    promiseToResolve: Promise<T>,
    options: {
      loading: { title: string };
      success: (data: T) => { title: string };
      error: (error: Error) => { title: string };
    }
  ) => {
    const loadingId = showToast({ ...options.loading, variant: 'loading', duration: 0 });

    try {
      const data = await promiseToResolve;
      hideToast(loadingId);
      showToast({ ...options.success(data), variant: 'success' });
      return data;
    } catch (error) {
      hideToast(loadingId);
      showToast({ ...options.error(error as Error), variant: 'error' });
      throw error;
    }
  }, [showToast, hideToast]);

  return (
    <ToastContext.Provider value={{ toasts, showToast, hideToast, promise }}>
      {children}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within ToastProvider');
  }
  return context;
}
